/**
 * Preview sanitizer (espejo defensivo del backend).
 *
 * El backend en verix-api/src/functions/ai/v2/previewSanitizer.ts ya sanea
 * los previews antes de enviarlos al cliente. Este modulo replica la logica
 * en el frontend como ULTIMA LINEA DE DEFENSA: si algun handler nuevo en
 * backend olvida pasar por el wrapper centralizado y filtra UUIDs/campos
 * de BD/jerga, aqui los bloqueamos antes de mostrarlos al usuario.
 *
 * Mantener sincronizado con previewSanitizer.ts del backend. Las claves
 * y diccionarios coinciden en ambos lados.
 */

// Claves prohibidas — nunca se muestran al usuario
export const ALWAYS_STRIP_KEYS = new Set([
  // Identificadores internos
  'id', 'company_id', 'user_id', 'tenant_id', 'workspace_id',
  'origin_id', 'parent_id', 'tool_use_id',
  // FKs contables / posting
  'journal_entry_id', 'reversal_entry_id', 'posted', 'posting_status',
  'audit_log_id', 'tiene_asiento',
  // Timestamps de sistema
  'created_at', 'updated_at', 'deleted_at',
  // Marcadores internos del wrapper de respuesta
  '_truncado', '_dedup', '_data_status', '_confianza', '_restrict_to_user_id',
  // Crypto / dedup interno
  'embedding', 'vector', 'hash', 'input_hash',
  'savepoint', 'sequence_nonce', 'idempotency_key',
]);

export const ID_KEY_RX = /(^id$|_id$|_uuid$|^uuid$)/i;
export const UUID_VALUE_RX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

// Renombrado de claves — snake_case interno → label humano ES
// Sincronizado con previewSanitizer.ts del backend.
export const KEY_LABELS = {
  concepto: 'Concepto', descripcion: 'Descripción', notas: 'Notas',
  motivo: 'Motivo', accion: 'Acción',
  numero: 'Número', referencia: 'Referencia',
  // Espejo de `previewSanitizer.ts`: sin estas entradas salía "Nif" y "Pais".
  nif: 'NIF', cif: 'CIF', nie: 'NIE', vat: 'VAT',
  nombre: 'Nombre', razon_social: 'Razón social',
  pais: 'País', ciudad: 'Ciudad', provincia: 'Provincia',
  direccion: 'Dirección', codigo_postal: 'Código postal',
  factura: 'Factura', cliente: 'Cliente', proveedor: 'Proveedor',
  empleado: 'Empleado', contacto: 'Contacto',
  total: 'Total', importe: 'Importe', subtotal: 'Subtotal',
  base: 'Base imponible', base_imponible: 'Base imponible',
  iva: 'IVA', cuota_iva: 'Cuota de IVA', impuestos: 'Impuestos',
  descuento: 'Descuento',
  estado: 'Estado', estado_actual: 'Estado actual', nuevo_estado: 'Nuevo estado',
  fecha: 'Fecha', fecha_emision: 'Fecha de emisión',
  fecha_vencimiento: 'Vencimiento', fecha_pago: 'Fecha de pago',
  fecha_cierre_esperada: 'Cierre esperado', vencimiento: 'Vencimiento',
  metodo: 'Método de pago', metodo_pago: 'Método de pago',
  cuenta: 'Cuenta bancaria', iban: 'IBAN', banco: 'Banco',
  cobro: 'Detalles del cobro', aviso_pago: 'Aviso',
  salario: 'Salario', neto: 'Neto', bruto: 'Bruto',
  bruto_mensual: 'Bruto mensual', salario_base_anual: 'Base anual',
  irpf: 'IRPF', ss: 'Seg. Social', deducciones: 'Deducciones',
  num_pagas: 'Número de pagas', grupo_cotizacion: 'Grupo cotización',
  categoria: 'Categoría', tarea: 'Tarea', proyecto: 'Proyecto',
  oportunidad: 'Oportunidad', etapa: 'Etapa', prioridad: 'Prioridad',
  responsable: 'Responsable', valor: 'Valor', probabilidad: 'Probabilidad',
  clave: 'Preferencia', email: 'Email', telefono: 'Teléfono',
  empresa: 'Empresa', cargo: 'Cargo', tipo: 'Tipo', asunto: 'Asunto',
  cambios: 'Cambios',
};

// Formateo europeo de números (mismo helper que el backend).
function formatEuropeanNumber(n, decimals = 2) {
  const fixed = Number(n).toFixed(decimals);
  const [intPart, decPart] = fixed.split('.');
  const withThousands = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  return decPart ? `${withThousands},${decPart}` : withThousands;
}

/** Devuelve true si la clave debe ocultarse del display al usuario. */
export function shouldHideKey(key) {
  if (ALWAYS_STRIP_KEYS.has(key)) return true;
  if (ID_KEY_RX.test(key)) return true;
  return false;
}

/** Devuelve true si el valor escalar parece un UUID y NO debe mostrarse. */
export function isUuidValue(value) {
  return typeof value === 'string' && UUID_VALUE_RX.test(value);
}

/** Mapea snake_case → Title Case con fallback al diccionario KEY_LABELS. */
export function humanizeKey(key) {
  if (KEY_LABELS[key]) return KEY_LABELS[key];
  // Solo la 1ª letra de cada palabra. `\p{L}` (Unicode) evita que un acento
  // cuente como frontera de palabra ("teléfono" → "Teléfono", no "TeléFono").
  return key
    .replace(/_/g, ' ')
    .replace(/(^|\s)\p{L}/gu, (m) => m.toUpperCase());
}

/**
 * Formatea un valor escalar para mostrar en el chip de preview.
 * - Numéricos con label tipo importe → "1.234,56 €"
 * - Numéricos sin label específico → "1.234"
 * - Booleanos → "Sí" / "No"
 * - String → tal cual
 */
export function formatPreviewValue(value, label) {
  if (typeof value === 'number') {
    const isCurrency = label
      ? /total|importe|subtotal|base|iva|salario|neto|bruto|coste|valor|cuota|descuento|saldo/i.test(label)
      : false;
    if (isCurrency) return formatEuropeanNumber(value, 2) + ' €';
    const isPct = label && /pct|rate|probabilidad|porcentaje|tipo/i.test(label) && value >= 0 && value <= 100;
    if (isPct) return `${value}%`;
    return formatEuropeanNumber(value, 0);
  }
  if (typeof value === 'boolean') return value ? 'Sí' : 'No';
  return String(value ?? '');
}

// ============================================================================
// ACTION_LABELS — actionType (SCREAMING_SNAKE_CASE) → label humano.
// Sin entrada, `humanizeActionType` cae a Title Case del nombre interno, que
// está en inglés: "Create Customer". Cubre los actionType que emite verix-api;
// el test de este módulo comprueba que no falte ninguno de los usados.
// ============================================================================

export const ACTION_LABELS = {
  // Accounting
  CREATE_INVOICE: 'Crear factura',
  UPDATE_INVOICE_STATUS: 'Cambiar estado de factura',
  CREATE_EXPENSE: 'Registrar gasto',
  DELETE_EXPENSE: 'Eliminar gasto',
  CANCEL_EXPENSE: 'Cancelar gasto',
  BULK_REMINDERS: 'Enviar recordatorios',
  EMIT_DRAFTS: 'Emitir facturas (lote)',
  POST_PERIODIFICATION: 'Periodificar gasto',
  POST_AGING_PROVISION: 'Dotar provisión por antigüedad',
  RECLASSIFY_LEASING: 'Reclasificar leasing',
  APPLY_CUSTOMER_ADVANCE: 'Aplicar anticipo de cliente',
  APPLY_SUPPLIER_ADVANCE: 'Aplicar anticipo a proveedor',
  POST_DEPRECIATION_BATCH: 'Registrar amortizaciones',
  POST_TAX_PROVISION: 'Dotar Impuesto de Sociedades',
  REPLAY_CLOSING: 'Reaplicar cierre mensual',

  // HR
  UPDATE_EMPLOYEE: 'Actualizar empleado',
  CALCULATE_PAYROLL: 'Generar nómina',
  CREATE_PAYROLL: 'Crear nómina',

  // Projects
  CREATE_TASK: 'Crear tarea',
  UPDATE_TASK: 'Actualizar tarea',
  CREATE_AUTOMATION: 'Crear automatización',
  BATCH_INVOICES: 'Marcar facturas (lote)',
  BATCH_TASKS: 'Completar tareas (lote)',
  BATCH_CONTACTS: 'Actualizar contactos (lote)',

  // CRM
  CREATE_CONTACT: 'Crear contacto',
  UPDATE_CONTACT: 'Actualizar contacto',
  CREATE_OPPORTUNITY: 'Crear oportunidad',
  UPDATE_OPPORTUNITY: 'Actualizar oportunidad',
  CLOSE_DEAL_WON: 'Cerrar oportunidad (ganada)',
  CLOSE_DEAL_LOST: 'Cerrar oportunidad (perdida)',
  CLOSE_OPPORTUNITY: 'Cerrar oportunidad',
  CREATE_QUOTE: 'Crear presupuesto',
  CREATE_CRM_ACTIVITY: 'Registrar actividad CRM',

  // Banking
  PSD2_CONNECT: 'Conectar banco',
  CONNECT_BANK: 'Conectar banco',
  SYNC_BANK: 'Sincronizar banco',
  CREATE_PAYMENT_LINK: 'Crear enlace de pago',
  PROCESS_REFUND: 'Procesar reembolso',

  // OCR / Currency
  OCR_FIELD_CORRECTION: 'Corregir campo de factura',
  OCR_REQUEST_RECTIFICATION: 'Solicitar rectificación al proveedor',
  OCR_PROMOTE_VENDOR_DRAFT: 'Validar proveedor detectado',
  CREATE_FX_INVOICE: 'Crear factura en divisa',

  // Preferences
  GUARDAR_PREFERENCIA: 'Guardar preferencia',
  GUARDAR_CONTEXTO_NEGOCIO: 'Guardar contexto del negocio',
  RECORDAR_CORRECCION: 'Recordar corrección',

  // ---------------------------------------------------------------------------
  // Auditoría 13-08. La cabecera de arriba decía "38 entradas cubriendo todos los
  // handlers backend" y ya no era cierto: de los 81 `actionType` que emite
  // verix-api, 44 no tenían etiqueta. La tarjeta de confirmación caía al fallback
  // —Title Case del nombre en SCREAMING_SNAKE— y mostraba "Create Customer" o
  // "Pay Payroll" en un producto en español, justo en las acciones de maestros
  // que más se usan.
  // ---------------------------------------------------------------------------

  // Maestros (clientes / proveedores)

  CREATE_CUSTOMER: 'Crear cliente',
  UPDATE_CUSTOMER: 'Actualizar cliente',
  CREATE_SUPPLIER: 'Crear proveedor',
  UPDATE_SUPPLIER: 'Actualizar proveedor',

  // Facturación y gastos
  SEND_INVOICE_EMAIL: 'Enviar factura por email',
  CREATE_INVOICE_RECTIFICATION: 'Crear factura rectificativa',
  RECTIFY_EXPENSE: 'Rectificar gasto',
  UPDATE_EXPENSE: 'Actualizar gasto',
  PAY_EXPENSE: 'Pagar gasto',
  UPDATE_QUOTE: 'Actualizar presupuesto',

  // Contabilidad
  CREATE_JOURNAL_ENTRY: 'Crear asiento',
  REVERSE_JOURNAL_ENTRY: 'Extornar asiento',
  SMART_RECONCILE: 'Conciliar movimientos',

  // RRHH
  CREATE_EMPLOYEE: 'Dar de alta un empleado',
  CREATE_CONTRACT: 'Crear contrato',
  UPDATE_CONTRACT: 'Actualizar contrato',
  CREATE_ABSENCE: 'Registrar ausencia',
  APPROVE_ABSENCE: 'Aprobar ausencia',
  CANCEL_PAYSLIP: 'Anular nómina',
  PAY_PAYSLIP: 'Pagar nómina',
  PAY_PAYROLL: 'Pagar nómina',

  // Catálogo e inventario
  CREATE_PRODUCT: 'Crear producto',
  UPDATE_PRODUCT: 'Actualizar producto',
  DELETE_PRODUCT: 'Eliminar producto',
  ADJUST_STOCK: 'Ajustar stock',
  TRANSFER_STOCK: 'Traspasar stock',
  RECORD_INVENTORY_IMPAIRMENT: 'Registrar deterioro de inventario',
  REVERSE_INVENTORY_IMPAIRMENT: 'Revertir deterioro de inventario',

  // Compras
  CREATE_PURCHASE_ORDER: 'Crear pedido de compra',
  RECEIVE_PURCHASE_ORDER: 'Recibir pedido de compra',
  CANCEL_PURCHASE_ORDER: 'Cancelar pedido de compra',
  UPDATE_PURCHASE_ORDER: 'Actualizar pedido de compra',

  // Ingresos recurrentes
  CREATE_AGREEMENT: 'Crear acuerdo recurrente',
  GENERATE_AGREEMENT_INVOICE: 'Generar factura del acuerdo',
  CREATE_REVENUE_UNIT: 'Crear unidad de ingresos',
  IMPUTE_EXPENSE_TO_UNIT: 'Imputar gasto a la unidad',
  LINK_ENTITY_TO_UNIT: 'Asociar a la unidad',

  // Maestros operativos y proyectos (verix-api #1954, #1956). El guard de este
  // módulo congela la lista de actionType del backend, así que NO cazó estos
  // seis — es la limitación que su propio comentario declara. Regenerada abajo.
  CREATE_WAREHOUSE: 'Crear almacén',
  UPDATE_WAREHOUSE: 'Actualizar almacén',
  CREATE_BANK_ACCOUNT: 'Crear cuenta bancaria',
  UPDATE_BANK_ACCOUNT: 'Actualizar cuenta bancaria',

  CREATE_PROJECT: 'Crear proyecto',
  UPDATE_PROJECT: 'Actualizar proyecto',

  // OCR / importación / workflows
  OCR_CREATE_EXPENSE: 'Crear gasto desde el documento',
  OCR_CREATE_INVOICE: 'Crear factura desde el documento',
  OCR_CREATE_PAYROLL: 'Crear nómina desde el documento',
  OCR_ARCHIVE_DOCUMENT: 'Archivar documento',
  IMPORT_TABLE: 'Importar tabla',
  EXECUTE_WORKFLOW: 'Ejecutar flujo de trabajo',

  // `programarAccion` (autonomousActions.ts) emite el actionType tal cual, en
  // minúsculas — no sigue la convención SCREAMING_SNAKE del resto de este
  // mapa. Sin esta entrada la tarjeta caía al fallback Title Case ("Programar
  // Accion"). El texto de la propia tarjeta (`preview`/`message`) ya distingue
  // un recordatorio personal de una vigilancia de negocio; esta etiqueta solo
  // cubre el título.
  programar_accion: 'Programar recordatorio o aviso',
};

/** Devuelve el label humano para un actionType, con fallback amigable. */
export function humanizeActionType(actionType) {
  if (!actionType || typeof actionType !== 'string') return 'Acción';
  if (ACTION_LABELS[actionType]) return ACTION_LABELS[actionType];
  // Fallback: SCREAMING_SNAKE_CASE → Title Case
  return actionType
    .toLowerCase()
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}
